// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
/* eslint-disable react/no-array-index-key */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { Typography } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import quickLinkCardStyles from "@werner/App/SharedStyle/MyAccount/QuickLinkCardStyles";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
    linkText = "linkText",
    image = "image",
    text = "text",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.linkText]: string;
        [fields.image]: string;
        [fields.text]: string;
        [fields.openInNewWindow]: boolean;
    };
    className?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    myAccountLinks: getPageLinkByPageType(state, "MyAccountPage")?.children?.filter(l => !l.excludeFromNavigation),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    affiliation: (state as xGoogleAnalyticsState).xGoogleAnalytics.affiliation,
});

export interface QuickLinkCardStyles {
    quickLinkWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    textWrapper?: InjectableCss;
    text?: TypographyProps;
    link?: LinkProps;
}

export const styles: QuickLinkCardStyles = {
    ...mergeWithCss(quickLinkCardStyles, {
        quickLinkWrapper: {
            css: css`
                &:not(.quick-links-widget) {
                    width: 100%;
                    max-width: 100%;
                    ${xScreen("lte", "md")} {
                        height: 144px;
                        width: 100%;
                        min-width: unset;
                    }
                }
            `,
        },
    }),
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const QuickLinkCard: FC<Props> = ({ fields, isAuthenticated, myAccountLinks, className, myAccountPageUrl, affiliation }) => {
    const isEditingInShell = useShellContext().isEditing;
    const link = useGetLink(fields.destination);

    if (myAccountPageUrl && link.url?.includes(myAccountPageUrl) && !myAccountLinks?.some(l => link.url === l.url)) {
        return null;
    }

    if (!isAuthenticated && !isEditingInShell) {
        return null;
    }

    return (
        <StyledWrapper {...styles.quickLinkWrapper} className={className}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={fields.image} {...styles.image} altText={fields.linkText} />
            </StyledWrapper>
            <StyledWrapper {...styles.textWrapper}>
                <Typography {...styles.text}>{fields.text}</Typography>
                <Link
                    href={link.url}
                    target={fields.openInNewWindow ? "_blank" : ""}
                    {...styles.link}
                    aria-label={`${fields.linkText}`}
                    onClick={() => (window as any)._GaMyAccountLinksClick(link, affiliation)}
                >
                    {fields.linkText}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(QuickLinkCard),
    definition: {
        group: "Xngage Custom" as any,
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.destination,
                displayName: "link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.linkText,
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.text,
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
        ],
    },
};

const QuickLinkCardComponent = connect(mapStateToProps)(QuickLinkCard);

export default widgetModule;
export { QuickLinkCardComponent };
